import React, { useState, useEffect, useRef } from "react";
import AgoraRTM from "agora-rtm-sdk";
import {
  getAgoraRtmToken,
  startTranscription,
  stopTranscription,
} from "js/Api/videocall";

const APP_ID = process.env.REACT_APP_AGORA_APP_ID;

const AgoraRTMTranscriptions = ({ 
  deviceId, 
  callingAccountId, 
  liveTranslation, 
  setMessages, 
  preferredLanguage = 'en'
}) => {
  const [rtmClient, setRtmClient] = useState(null);
  const [isRtmConnected, setIsRtmConnected] = useState(false);
  
  // Current subtitle state (matching Flutter)
  const [currentSubtitle, setCurrentSubtitle] = useState('');
  
  // Timer ref for clearing subtitles
  const clearTimerRef = useRef(null);

  const userInfo = localStorage.getItem("userInfo");

  // Helper function to send structured RTM messages (matching Flutter format)
  const sendRtmMessage = async (client, channelId, type, data, currentUserId) => {
    if (!client) {
      console.error('Cannot send RTM message: client not connected');
      return;
    }

    try {
      const message = JSON.stringify({
        type: type,
        data: {
          ...data,
          timestamp: Date.now(),
          sessionName: channelId,
          streamId: currentUserId,
        }
      });

      const publishOptions = {
        customType: 'PlainText',
        channelType: 'MESSAGE',
        storeInHistory: false
      };

      const result = await client.publish(channelId, message, publishOptions);
      console.log('Successfully sent RTM message:', data, result);
      return result;
    } catch (error) {
      console.error('Error sending RTM message:', error);
      throw error;
    }
  };

  // Handle RTM message (fix accumulation logic for typewriter effect)
  const handleRtmMessage = (message) => {
    try {
      const messageData = JSON.parse(message);
      
      if (messageData.type === 'transcribe') {
        const textContent = messageData.text;
        const innerData = JSON.parse(textContent);
        
        // Only process 'model_speech.transcription' events, ignore 'model_speech.transcription_delta'
        if (innerData.event === 'model_speech.transcription') {
          const payload = innerData.payload;
          const newText = payload.text?.toString().trim();
          const isFinal = payload.isFinal ?? false;
          const partyId = payload.partyId;
          
          const currentUserId = JSON.parse(userInfo).agoraUid;
          
          // Only filter out our own messages
          if (partyId === currentUserId) {
            return;
          }
          
          if (!newText) {
            return;
          }
          
          if (isFinal) {
            // For final messages, display the complete text and reset
            setCurrentSubtitle(newText);
            setMessages([newText]);
            
            // Clear timer if it exists
            if (clearTimerRef.current) {
              clearTimeout(clearTimerRef.current);
            }
            
            // Set new timer to clear subtitle after 5 seconds
            clearTimerRef.current = setTimeout(() => {
              setCurrentSubtitle('');
              setMessages([]);
            }, 5000);
          } else {
            // For non-final messages, accumulate text (typewriter effect)
            setCurrentSubtitle(prevSubtitle => {
              const accumulated = prevSubtitle ? `${prevSubtitle}${newText}` : newText;
              return accumulated;
            });
            
            // Update messages for real-time display
            setMessages(prev => {
              const currentText = prev.length > 0 ? prev[0] : '';
              const accumulated = currentText ? `${currentText}${newText}` : newText;
              return [accumulated];
            });
          }
        }
        // Handle other events like user_speech.started, model_speech.done etc.
        else if (innerData.event === 'user_speech.started') {
          // Clear previous subtitles when user starts speaking
          setCurrentSubtitle('');
          setMessages([]);
          if (clearTimerRef.current) {
            clearTimeout(clearTimerRef.current);
            clearTimerRef.current = null;
          }
        }
      } else if (messageData.type === 'control') {
        // Handle control messages
        // console.log('Control message received:', messageData.data);
      }
    } catch (error) {
      console.error('Error processing RTM message:', error);
    }
  };

  // Clear subtitles function
  const clearSubtitles = () => {
    setCurrentSubtitle('');
    setMessages([]);
    if (clearTimerRef.current) {
      clearTimeout(clearTimerRef.current);
      clearTimerRef.current = null;
    }
  };

  useEffect(() => {
    let isActive = true;
    
    if (liveTranslation) {
      (async () => {
        if (!userInfo || !isActive || !callingAccountId) {
          return;
        }
        
        const userId = JSON.parse(userInfo).userId;
        const agoraUid = JSON.parse(userInfo).agoraUid;

        try {
          // Call Start Transcription API
          const startTranscriptionMessage = await startTranscription(
            deviceId,
            userId
          );

          if (startTranscriptionMessage && isActive) {
            // Call Get Agora RTM token API
            const tokenResponse = await getAgoraRtmToken(deviceId);
            const { token } = tokenResponse;

            // Initiate Agora RTM using APP ID and Device ID
            const rtmClient = new AgoraRTM.RTM(APP_ID, deviceId);
            setRtmClient(rtmClient);

            // Call RTM Login
            const loginResult = await rtmClient.login({ token });
            console.log("Login result: ", loginResult);

            // Subscribe the channel
            const channelSubscribe = await rtmClient.subscribe(callingAccountId);
            console.log("Channel Subscribed Successfully", channelSubscribe);
            
            setIsRtmConnected(true);

            // Send START_RTM message (matching Flutter format)
            await sendRtmMessage(
              rtmClient,
              callingAccountId,
              'control',
              {
                action: 'START_RTM',
                languageCode: preferredLanguage,
                message: `User ${agoraUid} started RTM`
              },
              agoraUid
            );
          }
        } catch (error) {
          console.error("Error initializing RTM:", error);
          isActive = false;
        }
      })();
    } else {
      // Call stop transcription API
      isActive = false;
      (async () => {
        try {
          if (!rtmClient) return;
          
          const agoraUid = JSON.parse(userInfo).agoraUid;
          
          // Send STOP_RTM message before cleanup (matching Flutter format)
          if (rtmClient && callingAccountId && isRtmConnected) {
            await sendRtmMessage(
              rtmClient,
              callingAccountId,
              'control',
              {
                action: 'STOP_RTM',
                languageCode: preferredLanguage,
                message: `User ${agoraUid} stopped RTM`
              },
              agoraUid
            );
            
            // Wait a bit before cleanup (matching Flutter delay)
            await new Promise(resolve => setTimeout(resolve, 500));
          }
          
          // Cleanup Agora resources
          await rtmClient?.unsubscribe(callingAccountId);
          await rtmClient?.logout();
          
          setIsRtmConnected(false);
          
          // Clear subtitles
          clearSubtitles();
          
          // Then call stop API
          await stopTranscription(deviceId, JSON.parse(userInfo).userId);
          console.log("Transcription stopped");
          
          setRtmClient(null);
        } catch (error) {
          console.error("Cleanup error:", error);
        }
      })();
    }

    // Cleanup function
    return () => {
      isActive = false;
      clearSubtitles();
    };
  }, [userInfo, deviceId, callingAccountId, liveTranslation, preferredLanguage]);

  useEffect(() => {
    if (rtmClient && isRtmConnected) {
      rtmClient?.addEventListener("message", (event) => {
        if (event && event.message) {
          if (!isJson(event.message)) {
            console.warn("Received non-JSON message:", event.message);
            return;
          }

          // console.log('Received RTM message - Channel:', event.channelName, 'Message:', event.message);
          handleRtmMessage(event.message);
        }
      });

      // Handle connection state changes
      rtmClient?.addEventListener("ConnectionStateChanged", (newState, reason) => {
        console.log('RTM connection state changed:', newState, reason);
        const connected = newState === 'CONNECTED';
        setIsRtmConnected(connected);
        
        if (!connected) {
          clearSubtitles();
        }
      });
    }
  }, [rtmClient, isRtmConnected, userInfo]);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      clearSubtitles();
    };
  }, []);

  const isJson = (str) => {
    try {
      JSON.parse(str);
      return true;
    } catch (e) {
      return false;
    }
  };

  return <></>;
};

export default AgoraRTMTranscriptions;